import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import {
  HolidayVillage,
  GridViewRounded,
  AccessTimeOutlined,
  VerifiedUserOutlined,
  BadgeOutlined,
  FreeBreakfastOutlined,
  MoreTimeOutlined,
  CalendarMonthOutlined,
  CreditCardOutlined,
  ReceiptLongOutlined,
  EditCalendarOutlined,
  SwapHorizOutlined,
} from "@mui/icons-material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";
import { GlobalBreadcrumb } from "components/DesignSystem/sharedUiPatterns";
import { Leave } from "./Leave";
import { WorkAdjustment } from "./WorkAdjustment";
import { FilterProvider } from "../../FilterContext";
import { setFilterApi } from "services/Redux/userManagament";
import { designColors, designTheme, APP_BAR_HEIGHT } from "services/constants/designTokens";
import { RequestNavRail } from "components/CustomComponents/RequestNavRail";
import { Overtime } from "./Overtime";
import { OffDayDuty } from "./OffDayDuty";
import { FinanceRequests } from "./FinanceRequests";
import { AttendanceCorrection } from "./AttendanceCorrection";
import { ShiftCorrection } from "./ShiftCorrection";
import { MyRequestsHome } from "./MyRequestsHome";
import {
  checkViewerPermission,
  subModuleNameMatches,
} from "services/utils/HelperFunction";
// Shared with the Team Zone > Approve sidebar so the My Requests nav and the
// Approval Center nav are the same width by construction instead of two
// hand-copied numbers that can drift.
import { approveSidebarWidth as shiftSidebarWidth } from "pages/TeamZone/Approve/approveUi";

// Maps this screen's own internal selectedItem labels to their real "My
// Request" submodule name, for the two that don't already match one (see
// the subModuleId computation below for why this exists).
const SUBMODULE_LOOKUP_NAME_BY_SELECTED_ITEM = {
  "Myzone Attendance Correction": "Attendance Correction",
  "Myzone Shift Correction": "Shift Change",
};

export const MyZoneApply = () => {
  const [selectedItem, setSelectedItem] = useState("Home");
  const [selectedTab, setSelectedTab] = useState(null);
  const [attendanceTab, setAttendanceTab] = useState("permission");
  const [financeTab, setFinanceTab] = useState("advance");
  // The in-page category rail is now redundant on this page — the main app
  // Sidebar's own "My Requests" flyout (Sidebar/index.js, routes.js) covers
  // the same jump-to-a-category navigation, including its own "Home" entry
  // pointing at this same landing page. Same fix as Team Workspace's Approve
  // page (TeamZone/Approve/index.js) once its equivalent flyout shipped —
  // RequestNavRail's own width:0 transition still handles this (not an
  // unmount), so a future "actually, bring it back for X" is one value away
  // instead of re-adding the whole component.
  const sideBarOpen = false;
  const [showFullPage, setShowFullPage] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const { domain, userScreenAccess, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const dispatch = useDispatch();
  const shiftModule = userScreenAccess?.module_details?.find(
    (mod) => mod.module_name === "My Request",
  );

  // selectedItem uses this screen's own internal names, which don't always
  // match the real "My Request" submodule name -- "Myzone Attendance
  // Correction"/"Myzone Shift Correction" are UI-only labels, the real
  // submodules are "Attendance Correction"/"Shift Change" (same names each
  // screen's own checkEditorPermission call already uses, and the same ones
  // Sidebar/index.js's APPLY_CATEGORY_SUBMODULE map confirms via a live
  // payload). "Myzone WorkAdjustment" isn't remapped here because it was
  // never a real submodule at all for any of the four screens it covers
  // (Permission/On Duty/Comp Off/Break) -- each has its own real submodule,
  // and which one applies depends on the selected attendanceTab. That case is
  // resolved locally in WorkAdjustment/index.js (see TAB_SUBMODULE_NAME
  // there) instead of through this shared value.
  const submoduleLookupName =
    SUBMODULE_LOOKUP_NAME_BY_SELECTED_ITEM[selectedItem] || selectedItem;

  // Alias-aware match (SUB_MODULE_NAME_ALIASES in HelperFunction.js) -- a
  // literal `sub_module_name === submoduleLookupName` silently resolved to
  // undefined for any of these whose live payload spelling differs from
  // this screen's own name (e.g. "Leave Request"/"Salary Advance" instead
  // of "Leaves"/"Advance Salary" -- see Sidebar/index.js's
  // APPLY_CATEGORY_SUBMODULE comment, which already documents this exact
  // mismatch for the My Request module), even though each screen's own
  // checkEditorPermission-based access check already tolerates it. That
  // split (screen accessible, but no subModuleId) is what made Default
  // View/Save View look broken here: every filter-preference/Save-View
  // persist() and fetchSavedViews() call silently no-ops without a
  // subModuleId, so a saved default never loads and "Update" appears to
  // just close the popover with no effect.
  const subModuleId = shiftModule?.submodules?.find((sub) =>
    subModuleNameMatches(sub?.sub_module_name, submoduleLookupName),
  )?.sub_module_id;

  // Request navigation follows provisioning viewer access.
  const attendanceRows = {
    permission: {
      title: "Permission",
      key: "Myzone WorkAdjustment",
      tab: "permission",
      color: "#2878FF",
      icon: <VerifiedUserOutlined />,
    },
    onduty: {
      title: "On Duty",
      key: "Myzone WorkAdjustment",
      tab: "onduty",
      color: "#6D5DF6",
      icon: <BadgeOutlined />,
    },
    compoff: {
      title: "Comp Off",
      key: "Myzone WorkAdjustment",
      tab: "compoff",
      color: "#F08A24",
      icon: <AccessTimeOutlined />,
    },
    break: {
      title: "Break",
      key: "Myzone WorkAdjustment",
      tab: "break",
      color: "#0D9488",
      icon: <FreeBreakfastOutlined />,
    },
  };
  const attendanceTabAllowed = (tab) =>
    checkViewerPermission(
      userScreenAccess?.module_details,
      "My Request",
      attendanceRows[tab].title,
    );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const view = (
      params.get("view") ||
      params.get("section") ||
      ""
    ).toLowerCase();
    const tab = (params.get("tab") || params.get("type") || "").toLowerCase();

    if (view === "overtime" || view === "ot") {
      setSelectedItem("Myzone Overtime");
      if (tab === "pre-approval") {
        setSelectedTab("Pre-Approval");
      } else if (tab === "post-approval") {
        setSelectedTab("Post-Approval");
      } else {
        setSelectedTab(null);
      }
      return;
    }

    if (
      view === "attendance-correction" ||
      view === "attendancecorrection" ||
      view === "correction"
    ) {
      setSelectedItem("Myzone Attendance Correction");
      setSelectedTab(null);
      return;
    }

    if (
      view === "shift-correction" ||
      view === "shiftcorrection"
    ) {
      setSelectedItem("Myzone Shift Correction");
      setSelectedTab(null);
      return;
    }

    if (view === "work-adjustment" || view === "workadjustment") {
      setSelectedItem("Myzone WorkAdjustment");
      setSelectedTab(null);
      // Deep-links the WorkAdjustment screen straight to one of its own
      // attendance-type tabs (the flyout's Permission/On Duty/Comp
      // Off/Break rows each carry &tab=<key>) instead of always landing on
      // whichever tab was last selected in-page. Comp Off here is
      // deliberately a different tab of this same screen from the
      // standalone ?view=compoff/comp-off keys below, which open the
      // separate Off-Day Duty screen instead.
      if (
        tab === "permission" ||
        tab === "onduty" ||
        tab === "compoff" ||
        tab === "break"
      ) {
        setAttendanceTab(tab);
      }
      return;
    }

    if (
      view === "offday-duty" ||
      view === "offday" ||
      view === "compoff" ||
      view === "comp-off"
    ) {
      setSelectedItem("Myzone Off Day Duty");
      if (tab === "pre-approval") {
        setSelectedTab("Pre-Approval");
      } else if (tab === "post-approval") {
        setSelectedTab("Post-Approval");
      } else {
        setSelectedTab(null);
      }
      return;
    }

    if (
      view === "advance-salary" ||
      view === "advancesalary" ||
      view === "advance" ||
      view === "finance" ||
      view === "expense"
    ) {
      setSelectedItem("Advance Salary");
      setSelectedTab(null);
      if (view === "expense" || tab === "expense") {
        setFinanceTab("expense");
      } else if (tab === "advance" || tab === "salary-advance") {
        setFinanceTab("advance");
      }
      return;
    }

    if (view === "leave" || view === "leaves") {
      setSelectedItem("Leaves");
      setSelectedTab(null);
      return;
    }

    if (view === "attendance") {
      setSelectedItem("Myzone WorkAdjustment");
      setSelectedTab(null);
      return;
    }

    setSelectedItem("Home");
    setSelectedTab(null);
  }, [location.search, location.key]);

  // Grouped, flat sidebar: every request type is its own icon row, gathered
  // under an uppercase section header (My Requests / Leave & Time Off /
  // Attendance & Workday / Additional Work / Finance). No expandable parents —
  // clicking a row goes straight to its screen. A row with a `tab` opens a
  // sub-tab of a shared screen (attendance types share WorkAdjustment; Salary
  // Advance / Expense Claims share Advance Salary); a row without one is its own
  // screen.
  const requestSidebarSections = [
    {
      title: "My Requests",
      items: [
        {
          title: "Home",
          key: "Home",
          color: "#6D5DF6",
          icon: <GridViewRounded />,
        },
      ],
    },
    {
      title: "Leave & Time Off",
      items: [
        {
          title: "Leave Requests",
          key: "Leaves",
          color: "#6D5DF6",
          icon: <HolidayVillage />,
        },
        ...(attendanceTabAllowed("compoff")
          ? [attendanceRows.compoff]
          : []),
      ],
    },
    {
      title: "Attendance & Workday",
      items: [
        {
          title: "Attendance Correction",
          key: "Myzone Attendance Correction",
          color: "#12B76A",
          icon: <EditCalendarOutlined />,
        },
        {
          title: "Schedule Change",
          key: "Myzone Shift Correction",
          color: "#7A5AF8",
          icon: <SwapHorizOutlined />,
        },
        ...(attendanceTabAllowed("permission")
          ? [attendanceRows.permission]
          : []),
        ...(attendanceTabAllowed("onduty") ? [attendanceRows.onduty] : []),
        ...(attendanceTabAllowed("break") ? [attendanceRows.break] : []),
      ],
    },
    {
      title: "Additional Work",
      items: [
        {
          title: "Overtime",
          key: "Myzone Overtime",
          color: "#F08A24",
          icon: <MoreTimeOutlined />,
        },
        {
          title: "Off-Day Duty",
          key: "Myzone Off Day Duty",
          color: "#F08A24",
          icon: <CalendarMonthOutlined />,
        },
      ],
    },
    {
      title: "Finance",
      items: [
        {
          title: "Salary Advance",
          key: "Advance Salary",
          tab: "advance",
          color: "#2878FF",
          icon: <CreditCardOutlined />,
        },
        {
          title: "Reimbursement",
          key: "Advance Salary",
          tab: "expense",
          color: "#2878FF",
          icon: <ReceiptLongOutlined />,
        },
      ],
    },
  ];

  const itemIsActive = (item) => {
    if (item.tab) {
      const activeTab =
        item.key === "Advance Salary" ? financeTab : attendanceTab;
      return selectedItem === item.key && activeTab === item.tab;
    }
    return selectedItem === item.key;
  };

  const handleSidebarItemClick = (item) => {
    if (item.tab) {
      handleSubItemClick(item.key, item.tab);
    } else {
      handleShiftItemClick(item.key);
    }
  };

  const handleShiftItemClick = (itemTitle) => {
    setSelectedItem(itemTitle);
    dispatch(setFilterApi(true));
    setShowFullPage(false);
  };

  const handleSubItemClick = (parentKey, tab) => {
    setSelectedItem(parentKey);
    if (parentKey === "Advance Salary") {
      setFinanceTab(tab);
    } else {
      setAttendanceTab(tab);
    }
    dispatch(setFilterApi(true));
    setShowFullPage(false);
  };

  const renderMainContent = () => {
    switch (selectedItem) {
      case "Home":
        return <MyRequestsHome onNavigate={handleShiftItemClick} />;
      case "Leaves":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Leave subModuleId={subModuleId} />
          </FilterProvider>
        );
      case "Myzone Overtime":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <Overtime
              showFullPage={showFullPage}
              setShowFullPage={setShowFullPage}
              initialTab={selectedTab}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );
      case "Advance Salary":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <FinanceRequests
              initialTab={financeTab}
              showFullPage={showFullPage}
              setShowFullPage={setShowFullPage}
              subModuleId={subModuleId}
            />
          </FilterProvider>
        );
      case "Myzone Attendance Correction":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <AttendanceCorrection subModuleId={subModuleId} />
          </FilterProvider>
        );
      case "Myzone Shift Correction":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <ShiftCorrection subModuleId={subModuleId} />
          </FilterProvider>
        );
      case "Myzone WorkAdjustment":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <WorkAdjustment
              key={attendanceTab}
              initialTab={attendanceTab}
              // No subModuleId prop here -- WorkAdjustment now resolves its
              // own per-tab submodule id (see its own comment for why the
              // single shared value this screen computes above can't work
              // for its four tabs).
            />
          </FilterProvider>
        );
      case "Myzone Off Day Duty":
        return (
          <FilterProvider
            domain={domain}
            userDetails={userDetails}
            subModuleId={subModuleId}
          >
            <OffDayDuty initialTab={selectedTab} subModuleId={subModuleId} />
          </FilterProvider>
        );
      default:
        return null;
    }
  };

  // The last breadcrumb segment names the request screen currently open. The
  // attendance types share the WorkAdjustment screen (told apart by
  // attendanceTab) and Salary Advance / Expense Claims share Advance Salary
  // (told apart by financeTab), so those two are mapped explicitly; the rest
  // read straight off `selectedItem`. On the My Requests landing there is no
  // leaf — the trail ends at "My Requests" itself.
  const breadcrumbLeaf = () => {
    switch (selectedItem) {
      case "Leaves":
        return "Leave Request";
      case "Myzone Overtime":
        return "Overtime";
      case "Myzone Off Day Duty":
        return "Off-Day Duty";
      case "Myzone Attendance Correction":
        return "Attendance Correction";
      case "Myzone Shift Correction":
        return "Schedule Change";
      case "Myzone WorkAdjustment":
        return (
          {
            permission: "Permission",
            onduty: "On Duty",
            compoff: "Compoff",
            break: "Break",
          }[attendanceTab] || "Attendance"
        );
      case "Advance Salary":
        return financeTab === "expense" ? "Expense" : "Advance Requests";
      default:
        return null;
    }
  };

  const breadcrumbLeafLabel = breadcrumbLeaf();

  return (
    <ThemeProvider theme={designTheme}>
      <Box
        sx={{
          display: "flex",
          // Sidebar renders an 80px header spacer and a 1px divider in flow.
          // Account for both so short request lists do not scroll the page.
          height: { xs: "auto", md: `calc(100vh - ${APP_BAR_HEIGHT + 1}px)` },
          minHeight: `calc(100vh - ${APP_BAR_HEIGHT + 1}px)`,
          backgroundColor: designColors.pageBg,
        }}
      >
        <RequestNavRail
          sections={requestSidebarSections}
          width={shiftSidebarWidth}
          sideBarOpen={sideBarOpen}
          isItemActive={itemIsActive}
          onItemClick={handleSidebarItemClick}
        />

        <Box
          sx={{
            flexGrow: 1,
            p: { xs: 1.5, md: 2 },
            minWidth: 0,
            minHeight: 0,
            overflowY: { xs: "visible", md: "auto" },
            overflowX: "hidden",
            backgroundColor: designColors.pageBg,
            transition: "all 0.3s ease",
            // Keep the content scrollable but hide the visible scrollbar.
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE/Edge
            "&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
          }}
        >
          {!showFullPage ? (
            <GlobalBreadcrumb
              sx={{ mb: 1.5 }}
              trail={[
                {
                  label: "My Workspace",
                  onClick: () => history.push("/admin/myzone/status"),
                },
                breadcrumbLeafLabel
                  ? {
                      label: "My Requests",
                      onClick: () => handleShiftItemClick("Home"),
                    }
                  : "My Requests",
                ...(breadcrumbLeafLabel ? [breadcrumbLeafLabel] : []),
              ]}
            />
          ) : null}
          {renderMainContent()}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
